import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const LanguageContext = createContext(null);

const translations = {
  es: {
    // Navigation
    dashboard: 'Panel de Control',
    clients: 'Clientes',
    plans: 'Planes',
    exercises: 'Ejercicios',
    settings: 'Configuración',
    logout: 'Cerrar Sesión',
    
    // Auth
    login: 'Iniciar Sesión',
    email: 'Correo Electrónico',
    password: 'Contraseña',
    welcomeBack: 'Bienvenido de nuevo',
    enterCredentials: 'Ingresa tus credenciales para acceder',
    
    // Dashboard
    totalClients: 'Total Clientes',
    totalPlans: 'Total Planes',
    totalExercises: 'Total Ejercicios',
    recentActivity: 'Actividad Reciente',
    quickActions: 'Acciones Rápidas',
    
    // Clients
    newClient: 'Nuevo Cliente',
    editClient: 'Editar Cliente',
    deleteClient: 'Eliminar Cliente',
    clientName: 'Nombre del Cliente',
    phone: 'Teléfono',
    notes: 'Notas',
    assignedPlans: 'Planes Asignados',
    noClients: 'No hay clientes registrados',
    
    // Plans
    newPlan: 'Nuevo Plan',
    editPlan: 'Editar Plan',
    deletePlan: 'Eliminar Plan',
    planName: 'Nombre del Plan',
    objective: 'Objetivo',
    description: 'Descripción',
    weeks: 'Semanas',
    sessions: 'Sesiones',
    noPlans: 'No hay planes creados',
    
    // Exercises
    newExercise: 'Nuevo Ejercicio',
    editExercise: 'Editar Ejercicio',
    deleteExercise: 'Eliminar Ejercicio',
    exerciseName: 'Nombre del Ejercicio',
    videoUrl: 'URL del Video',
    sets: 'Series',
    reps: 'Repeticiones',
    time: 'Tiempo',
    rest: 'Descanso',
    noExercises: 'No hay ejercicios en la biblioteca',
    
    // Common
    save: 'Guardar',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    edit: 'Editar',
    add: 'Agregar',
    search: 'Buscar',
    filter: 'Filtrar',
    actions: 'Acciones',
    confirm: 'Confirmar',
    back: 'Volver',
    loading: 'Cargando...',
    success: 'Éxito',
    error: 'Error',
    
    // Client Portal
    myTraining: 'Mi Entrenamiento',
    currentPlan: 'Plan Actual',
    viewSession: 'Ver Sesión',
    markComplete: 'Marcar Completado',
    addFeedback: 'Agregar Comentario',
    
    // Messages
    clientCreated: 'Cliente creado exitosamente',
    clientUpdated: 'Cliente actualizado exitosamente',
    clientDeleted: 'Cliente eliminado exitosamente',
    planCreated: 'Plan creado exitosamente',
    planUpdated: 'Plan actualizado exitosamente',
    planDeleted: 'Plan eliminado exitosamente',
    planAssigned: 'Plan asignado exitosamente',
    exerciseCreated: 'Ejercicio creado exitosamente',
    exerciseUpdated: 'Ejercicio actualizado exitosamente',
    exerciseDeleted: 'Ejercicio eliminado exitosamente',
    loginError: 'Credenciales inválidas',
    
    // Week/Session
    week: 'Semana',
    session: 'Sesión',
    addWeek: 'Agregar Semana',
    addSession: 'Agregar Sesión',
    addExerciseToSession: 'Agregar Ejercicio',
    
    // Assign
    assignPlan: 'Asignar Plan',
    unassignPlan: 'Desasignar Plan',
    selectPlan: 'Seleccionar Plan',
    selectClient: 'Seleccionar Cliente',
  },
  en: {
    // Navigation
    dashboard: 'Dashboard',
    clients: 'Clients',
    plans: 'Plans',
    exercises: 'Exercises',
    settings: 'Settings',
    logout: 'Logout',
    
    // Auth
    login: 'Login',
    email: 'Email',
    password: 'Password',
    welcomeBack: 'Welcome Back',
    enterCredentials: 'Enter your credentials to access',
    
    // Dashboard
    totalClients: 'Total Clients',
    totalPlans: 'Total Plans',
    totalExercises: 'Total Exercises',
    recentActivity: 'Recent Activity',
    quickActions: 'Quick Actions',
    
    // Clients
    newClient: 'New Client',
    editClient: 'Edit Client',
    deleteClient: 'Delete Client',
    clientName: 'Client Name',
    phone: 'Phone',
    notes: 'Notes',
    assignedPlans: 'Assigned Plans',
    noClients: 'No clients registered',
    
    // Plans
    newPlan: 'New Plan',
    editPlan: 'Edit Plan',
    deletePlan: 'Delete Plan',
    planName: 'Plan Name',
    objective: 'Objective',
    description: 'Description',
    weeks: 'Weeks',
    sessions: 'Sessions',
    noPlans: 'No plans created',
    
    // Exercises
    newExercise: 'New Exercise',
    editExercise: 'Edit Exercise',
    deleteExercise: 'Delete Exercise',
    exerciseName: 'Exercise Name',
    videoUrl: 'Video URL',
    sets: 'Sets',
    reps: 'Reps',
    time: 'Time',
    rest: 'Rest',
    noExercises: 'No exercises in library',
    
    // Common
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    search: 'Search',
    filter: 'Filter',
    actions: 'Actions',
    confirm: 'Confirm',
    back: 'Back',
    loading: 'Loading...',
    success: 'Success',
    error: 'Error',
    
    // Client Portal
    myTraining: 'My Training',
    currentPlan: 'Current Plan',
    viewSession: 'View Session',
    markComplete: 'Mark Complete',
    addFeedback: 'Add Feedback',
    
    // Messages
    clientCreated: 'Client created successfully',
    clientUpdated: 'Client updated successfully',
    clientDeleted: 'Client deleted successfully',
    planCreated: 'Plan created successfully',
    planUpdated: 'Plan updated successfully',
    planDeleted: 'Plan deleted successfully',
    planAssigned: 'Plan assigned successfully',
    exerciseCreated: 'Exercise created successfully',
    exerciseUpdated: 'Exercise updated successfully',
    exerciseDeleted: 'Exercise deleted successfully',
    loginError: 'Invalid credentials',
    
    // Week/Session
    week: 'Week',
    session: 'Session',
    addWeek: 'Add Week',
    addSession: 'Add Session',
    addExerciseToSession: 'Add Exercise',
    
    // Assign
    assignPlan: 'Assign Plan',
    unassignPlan: 'Unassign Plan',
    selectPlan: 'Select Plan',
    selectClient: 'Select Client',
  }
};

export const LanguageProvider = ({ children }) => {
  const { user, updateLanguage: updateUserLanguage } = useAuth();
  const [language, setLanguage] = useState(() => {
    // 1. localStorage
    const stored = localStorage.getItem('language');
    if (stored === 'es' || stored === 'en') return stored;
    // 2. navegador (es-ES, es-MX, es-AR → 'es')
    try {
      const nav = (navigator?.language || navigator?.userLanguage || '').toLowerCase();
      if (nav.startsWith('es')) return 'es';
      if (nav.startsWith('en')) return 'en';
    } catch (_) { /* ignore */ }
    // 3. por defecto ES (target Spain market)
    return 'es';
  });

  useEffect(() => {
    if (user?.language) {
      setLanguage(user.language);
      localStorage.setItem('language', user.language);
    }
  }, [user?.language]);

  const t = (key) => {
    return translations[language]?.[key] || translations['es'][key] || key;
  };

  const changeLanguage = async (newLang) => {
    setLanguage(newLang);
    localStorage.setItem('language', newLang);
    if (user) {
      await updateUserLanguage(newLang);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, t, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
